import { Modal, ModalProps } from 'antd';
import { useLabel } from '@knowledge/common/hooks/useGroup';
import { LoadingOutlined } from '@ant-design/icons';
import KnowledgeListForm, { KnowledgeListFormProps } from '../KnowledgeListForm';
import styled from '@emotion/styled';
import LogisticsFormContent from '../KnowledgeListForm/LogisticsFormContent';
import { Store } from 'components/StoreScopes';
import { useLogisticsDrawer } from '../KnowledgeListForm/LogisticsEditFormDrawer';

export interface KnowledgeListFormModalProps<T extends Knowledge.KnowledgeType>
  extends ModalProps,
    Util.PartialPartial<
      Pick<KnowledgeListFormProps<T>, 'knowledgeType' | 'initialValues' | 'onSubmit' | 'onEdit'>,
      'initialValues'
    > {
  isShowLogisticsFormContent?: boolean;
  stores: Store[];
  onUpdate: () => void;
}

function KnowledgeListFormModal<T extends Knowledge.KnowledgeType>(
  props: KnowledgeListFormModalProps<T>,
) {
  const {
    knowledgeType,
    initialValues,
    isShowLogisticsFormContent,
    stores,
    onUpdate,
    onSubmit,
    onEdit,
    ...otherProps
  } = props;

  const { t, knowledgeLabel } = useLabel();

  const title = knowledgeLabel[knowledgeType];

  const modalProps: ModalProps = {
    title,
    width: 1200,
    ...otherProps,
  };

  const { logisticsDrawerState, logisticsDrawer } = useLogisticsDrawer({
    type: knowledgeType,
    onUpdate,
  });

  if (!initialValues) {
    return (
      <Modal footer={false} {...modalProps}>
        <LoadingOutlined />
      </Modal>
    );
  }

  return (
    <KnowledgeListForm
      knowledgeType={knowledgeType}
      initialValues={initialValues}
      onSubmit={onSubmit}
      onEdit={onEdit}
    >
      {(form, props) => {
        const { dirty, isSubmitting, handleSubmit } = props;

        return (
          <Modal
            okText={t('marketing.保存')}
            okButtonProps={{
              disabled: !dirty,
              loading: isSubmitting,
              onClick: () => handleSubmit(),
            }}
            {...modalProps}
          >
            <Content>
              {isShowLogisticsFormContent ? (
                <LogisticsFormContent
                  type={knowledgeType}
                  allStores={stores}
                  onEdit={(initialValues) => logisticsDrawerState.show({ initialValues })}
                />
              ) : (
                form
              )}
            </Content>
            {logisticsDrawer}
          </Modal>
        );
      }}
    </KnowledgeListForm>
  );
}

const Content = styled.div`
  max-height: calc(100vh - 205px);
  margin: -24px;
  padding: 16px;
  overflow: auto;
  > * + * {
    margin-top: 16px;
  }
`;

export default KnowledgeListFormModal;
